/**
 * @file Renders an investment's profile as a description list, with its errors and a footer.
 */
import React from "react";
import { Descriptions, Space } from "antd";
import type { ReactNode } from "react";

import InvestmentErrors from "./InvestmentErrors";
import renderInvestmentItems from "./helpers/renderInvestmentItems";
import type { Tier, Investment } from "./helpers/types";

const { Item } = Descriptions;

const COLUMNS_COUNT = 2;

type ProfileInvestment = Investment & {
  isFunded?: boolean;
  profileErrors?: string[];
};

type Props = {
  tiers: Tier[];
  footer?: ReactNode;
  investment: ProfileInvestment;
};

/**
 * Renders an investment's profile as a description list, with its errors and a footer.
 */
const InvestmentProfile = ({
  footer = null,
  tiers,
  investment,
}: Props): JSX.Element => {
  const items = renderInvestmentItems(tiers, investment);

  const {
    isFunded = false,
    profileErrors = [],
  } = investment;

  return (
    <Space direction="vertical">
      <Descriptions
        size="small"
        column={COLUMNS_COUNT}
        layout="vertical"
        className="summary"
      >
        {
          items.map(({ value, ...itemProps }, key) => (
            <Item key={key} {...itemProps}>
              {value}
            </Item>
          ))
        }
      </Descriptions>

      <InvestmentErrors
        errors={profileErrors}
        isFunded={isFunded}
      />

      {footer}
    </Space>
  );
};

export default InvestmentProfile;
